import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { generatePairingCode, submitPairingCode } from '../../../features/auth/pairing-stub';

// Feature flag (off in dev, on only when the multi-device phase ships).
// In Phase 4 the screen is unreachable from the nav — this route lands the
// UI layout so when the engine arrives, only the stub swap is needed.
const PAIRING_ENABLED = false;

function PairingPage() {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!PAIRING_ENABLED) {
    return (
      <main className="p-6 max-w-2xl">
        <h1 className="text-2xl font-display">Add another device</h1>
        <p className="text-sm mt-2">
          Multi-device pairing is coming in a later release. The pairing engine uses hybrid X25519 +
          ML-KEM-768 — see{' '}
          <a href="/security" className="underline">
            /security
          </a>{' '}
          for the protocol summary.
        </p>
      </main>
    );
  }

  async function onGenerate() {
    setBusy(true);
    setError(null);
    try {
      await generatePairingCode();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'pairing failed');
    } finally {
      setBusy(false);
    }
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const fd = new FormData(e.currentTarget);
      const codeRaw = fd.get('code');
      const sasRaw = fd.get('sas');
      const code = typeof codeRaw === 'string' ? codeRaw : '';
      const sas = typeof sasRaw === 'string' ? sasRaw : '';
      await submitPairingCode(code, sas);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'pairing failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="p-6 max-w-2xl space-y-8">
      <h1 className="text-2xl font-display">Add another device</h1>

      <section className="space-y-2">
        <h2 className="text-sm uppercase text-fg/60">On this device — show the code</h2>
        <button
          type="button"
          onClick={() => void onGenerate()}
          disabled={busy}
          className="bg-accent text-bg px-3 py-1"
        >
          Generate pairing code
        </button>
      </section>

      <section className="space-y-2">
        <h2 className="text-sm uppercase text-fg/60">On the new device — enter the code</h2>
        <form onSubmit={(e) => void onSubmit(e)} className="space-y-2">
          <label className="block text-sm">
            9-digit pairing code
            <input
              name="code"
              type="text"
              inputMode="numeric"
              maxLength={9}
              className="w-full border px-2 py-1 mt-1 font-mono"
            />
          </label>
          <label className="block text-sm">
            3-digit confirmation
            <input
              name="sas"
              type="text"
              inputMode="numeric"
              maxLength={3}
              className="w-full border px-2 py-1 mt-1 font-mono"
            />
          </label>
          <button type="submit" disabled={busy} className="border px-3 py-1">
            Submit
          </button>
        </form>
      </section>

      {error && (
        <p role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}
    </main>
  );
}

export const Route = createFileRoute('/_authed/app/settings/pairing')({
  component: PairingPage,
});
